// A cloud provider is registered as several integration keys so the server can
// gate and price its block families separately — GCP as gcp/gcp-cost/
// gcp-security/gcp-data, AWS as aws/aws-cost. Users think in providers, not
// families, so the UI collapses them:
//
//   - the builder sidebar shows one "Google Cloud" section rather than four
//   - the workflow list filter offers one option per provider
//
// This is presentation only. Each action keeps its own integration value, which
// is what the drag payload carries and what the executor dispatches on, so a
// dropped block still reaches the handler registered for its real integration
// and pricing is unaffected.

// sidebarGroupFor maps an integration to the sidebar section it belongs under.
//
// GCP Cost stays its own section, mirroring the separate AWS "Cloud Cost"
// section, because cost is the one family gated on a BigQuery billing export
// rather than the connection itself.
export function sidebarGroupFor(integration: string): string {
  if (integration === 'gcp-security' || integration === 'gcp-data') return 'gcp';
  if (integration === 'aws-ops' || integration === 'aws-security' || integration === 'aws-data') return 'aws';
  return integration;
}

// filterGroupFor maps an integration to the provider used by the workflow list
// filter.
//
// Unlike the sidebar this also folds the cost families in, so filtering by
// "AWS" or "Google Cloud" finds every workflow touching that provider. The AWS
// filter previously matched only the 'aws' key while Cloud Cost blocks are
// tagged 'aws-cost', and no 'aws-cost' option existed — so cost workflows could
// not be found at all.
export function filterGroupFor(integration: string): string {
  if (integration.startsWith('aws')) return 'aws';
  if (integration.startsWith('gcp')) return 'gcp';
  return integration;
}

// AI-driven cloud blocks (LLM investigation, RCA + fix generation, IaC
// generation and the CLI executor that applies its output) sit at the bottom
// of the AWS and Google Cloud sections. The deterministic read/remediate
// blocks are what users reach for first; the AI blocks are the "let Kestrel
// work it out" fallbacks, so they close the list rather than open it.
const TRAILING_CLOUD_AI_BLOCKS = new Set([
  // AWS
  'kestrel-investigate-cloud',
  'kestrel-trigger-cloud-rca',
  'kestrel-generate-cloud-resource',
  'kestrel-execute-cloud-cli',
  // GCP
  'gcp-investigate',
  'gcp-trigger-cloud-rca',
  'gcp-generate-cloud-resource',
  'gcp-execute-cloud-cli',
]);

// orderSidebarActions returns the actions for one sidebar section in display
// order: catalog order, except that the cloud AI blocks move to the end (still
// in their catalog order relative to each other). Sections without AI blocks
// come back unchanged. Non-mutating.
export function orderSidebarActions<T extends { id: string }>(sectionKey: string, actions: T[]): T[] {
  if (sectionKey !== 'aws' && sectionKey !== 'gcp') return actions;
  const leading: T[] = [];
  const trailing: T[] = [];
  for (const a of actions) {
    (TRAILING_CLOUD_AI_BLOCKS.has(a.id) ? trailing : leading).push(a);
  }
  return trailing.length === 0 ? actions : [...leading, ...trailing];
}
